import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Styled from './styled';
// components
import {
    Badge,
    BadgeProps,
    MoreMenu,
    MenuItemProps,
} from '../index';
// static assets
import {
    CloseIcon,
    MenuIcon,
    RingIconOld,
} from '../index';

export type HeaderProps = {
    // accessibility support (alt text/aria-label, defaults to 'More Menu')
    ariaLabelMenu?: string;
    // accessibility support (alt text/aria-label, defaults to 'Minimize')
    ariaLabelMinimize?: string;
    /** Optional Badge Properties */
    badgeProps?: BadgeProps;
    /** Optional collapse button click handler. */
    onCollapse?: () => void;
    /** List of menu items to be displayed in the header's menu. */
    menuItems: MenuItemProps[];
    /**
     * Optional portal container for rendering the MoreMenu.
     * When provided, the MoreMenu is rendered within the same stacking context
     * as the SessionContainer, preventing issues such as the Body appearing above the ChatHeader menu on iOS.
     */
    menuPortalElement?: HTMLDivElement;
    /** Optional boolean which determines whether or not we display the badge (defaults to true) */
    showBadge?: boolean;
};

/**
 * Header component that displays a chat header with a status indicator, menu button, and optional collapse button.
 * The menu can be toggled and supports closing when clicking outside of it.
 *
 * @param {HeaderProps} props - The properties to configure the Header component.
 * @returns {JSX.Element} The rendered Header component.
 */
export const Header: React.FC<HeaderProps> = ({
    ariaLabelMenu = 'More Menu',
    ariaLabelMinimize = 'Minimize',
    badgeProps = {
        title: 'Connected',
        type: 'solid',
        color: 'primary',
    },
    onCollapse,
    menuItems,
    menuPortalElement,
    showBadge = true,
}) => {

    const [isMenuVisible, setMenuVisible] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);

    /**
     * Handles clicking outside the menu to close it.
     *
     * @param {MouseEvent | TouchEvent} event - The mouse or touch event.
     */
    const handleClickOutside = useCallback((event: MouseEvent | TouchEvent) => {
        if (
            menuRef.current &&
            !menuRef.current.contains(event.target as Node) &&
            buttonRef.current &&
            !buttonRef.current.contains(event.target as Node)
        ) {
            setMenuVisible(false);
        }
    }, []);

    // Effect to add or remove event listeners based on menu visibility
    useEffect(() => {
        if (isMenuVisible) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        }

        // Cleanup event listeners on component unmount
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isMenuVisible, handleClickOutside]);

    /**
     * Handles the click event on the menu button to toggle the menu visibility.
     *
     * @param {React.MouseEvent} event - The mouse event.
     */
    const handleMenuButtonClick = (event: React.MouseEvent | React.KeyboardEvent) => {
        event.stopPropagation(); // Prevents the click event from propagating to the document
        setMenuVisible(!isMenuVisible);
    };

    return (
        <Styled.HeaderWrapper>
            <Styled.ChatHeader>
                <Styled.HeaderIconWrapper>
                    <Styled.HeaderIcon>
                        <RingIconOld />
                    </Styled.HeaderIcon>
                </Styled.HeaderIconWrapper>
                <Styled.ChatStatusIndicator>
                    {showBadge && <Badge {...badgeProps} />}
                </Styled.ChatStatusIndicator>
                <Styled.MenuButton
                    ref={buttonRef}
                    onClick={handleMenuButtonClick}
                    tabIndex={1}
                    role='button'
                    aria-label={ariaLabelMenu}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault(); // Prevent default scrolling for Space key
                            handleMenuButtonClick(e);
                        }
                    }}
                >
                    <MenuIcon />
                </Styled.MenuButton>
                <Styled.CloseButton
                    onClick={onCollapse}
                    tabIndex={3}
                    role='button'
                    aria-label={ariaLabelMinimize}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onCollapse?.();
                        }
                    }}
                >
                    <CloseIcon />
                </Styled.CloseButton>
            </Styled.ChatHeader>
            <Styled.MenuContainer>
                {isMenuVisible && (menuPortalElement ?
                    createPortal(
                        <MoreMenu
                            className={isMenuVisible ? 'expanded' : 'menu-portal'}
                            menuItems={menuItems}
                            ref={menuRef}
                            setMenuVisible={setMenuVisible}
                        />,
                        menuPortalElement
                    ) :
                    <MoreMenu
                        className={isMenuVisible ? 'expanded' : 'menu-standard'}
                        menuItems={menuItems}
                        ref={menuRef}
                        setMenuVisible={setMenuVisible}
                    />
                )}
            </Styled.MenuContainer>
        </Styled.HeaderWrapper>
    );
};
